import { safeLocalStorage } from '@/lib/safeStorage';
import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import {
  fetchAiConversations,
  saveAiConversation,
  deleteAiConversation,
  type AiConversationSummary,
} from '@/lib/api/aiConversations';
import type { AiConversationSource, AssistantBlockView, AssistantReplay } from '@ruta/shared';
import type { AssistantEntityReference } from '@/lib/api/assistantMentions';

export interface NavigationAction {
  label: string;
  path: string;
  state: Record<string, unknown>;
  variant?: 'primary' | 'secondary';
  autoNavigate?: boolean;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /**
   * Short label shown in the chat when `content` is a long hidden brief
   * (writing-help seeds). The model still receives `content`.
   */
  displayContent?: string;
  suggestions?: Array<{ label: string; prompt: string }>;
  navigationActions?: NavigationAction[];
  /** Set when the assistant previewed an action and is waiting for the user to confirm/cancel. */
  pendingConfirmation?: boolean;
  /** Preview copy from the tool (e.g. labeled Bug / Suggestion / Feedback). */
  confirmationPrompt?: string;
  /** Tool the Confirm button re-runs. Absent after the card is dismissed. */
  pendingReplay?: AssistantReplay;
  /** Show Retry instead of Confirm — the last confirmed send did not file. */
  pendingRetry?: boolean;
  /** Visit, customer, map, and review cards from this turn. */
  blocks?: AssistantBlockView[];
  /** Tool names that ran for this turn. */
  toolNames?: string[];
  /** Live activity line. Shown only while the reply has no text yet. */
  activityLabel?: string | null;
  /** Entities the user @-attached to this message (shown as chips on the user bubble). */
  references?: AssistantEntityReference[];
  /**
   * How many data tools the assistant called to produce this reply (RUT-2673).
   * Kept on the message for provenance; the bubble does not show a
   * "Looking up…" footer after the turn finishes.
   */
  toolCalls?: number;
  /** True when this bubble is an error / empty-turn explanation, not a draft. */
  failed?: boolean;
  createdAt?: Date;
}

export interface SavedConversation {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: Date;
  updatedAt: Date;
  /** Which app the chat was started in — undefined on pre-existing chats. */
  source?: AiConversationSource;
}

const STORAGE_KEY = 'ruta-chat-history';
const MAX_CONVERSATIONS = 50;

function loadConversationsFromStorage(): SavedConversation[] {
  try {
    const stored = safeLocalStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return parsed.map((c: SavedConversation) => ({
      ...c,
      createdAt: new Date(c.createdAt),
      updatedAt: new Date(c.updatedAt),
      messages: c.messages.map((m) => ({
        ...m,
        createdAt: m.createdAt ? new Date(m.createdAt) : undefined,
      })),
    }));
  } catch {
    return [];
  }
}

function saveConversationsToStorage(conversations: SavedConversation[]) {
  try {
    safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(conversations.slice(0, MAX_CONVERSATIONS)));
  } catch {
    // Storage full or unavailable
  }
}

function generateTitle(messages: ChatMessage[]): string {
  const firstUserMessage = messages.find((m) => m.role === 'user');
  if (!firstUserMessage) return 'New conversation';
  const content = (firstUserMessage.displayContent ?? firstUserMessage.content).trim();
  return content.length > 50 ? content.slice(0, 50) + '...' : content;
}

function conversationToSummary(conversation: SavedConversation): AiConversationSummary {
  return {
    id: conversation.id,
    title: conversation.title,
    messages: conversation.messages.map((m) => ({
      role: m.role,
      content: m.content,
      createdAt: m.createdAt?.toISOString() ?? new Date().toISOString(),
      ...(m.displayContent ? { displayContent: m.displayContent } : {}),
    })),
    createdAt: conversation.createdAt.toISOString(),
    updatedAt: conversation.updatedAt.toISOString(),
    source: conversation.source,
  };
}

function summaryToConversation(summary: AiConversationSummary): SavedConversation {
  return {
    id: summary.id,
    title: summary.title,
    messages: summary.messages.map((m) => ({
      id: `${summary.id}-${m.role}-${m.createdAt}`,
      role: m.role,
      content: m.content,
      createdAt: new Date(m.createdAt),
      ...(m.displayContent ? { displayContent: m.displayContent } : {}),
    })),
    createdAt: new Date(summary.createdAt),
    updatedAt: new Date(summary.updatedAt),
    source: summary.source,
  };
}

interface ChatContextValue {
  messages: ChatMessage[];
  currentConversationId: string | null;
  addMessage: (message: ChatMessage) => void;
  updateMessage: (id: string, updates: Partial<ChatMessage>) => void;
  clearMessages: () => void;
  conversations: SavedConversation[];
  loadConversation: (id: string) => void;
  deleteConversation: (id: string) => void;
  startNewConversation: () => void;
  /** Re-pull server history (server is authoritative — reflects deletes from other devices). */
  refreshConversations: () => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export function ChatProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<SavedConversation[]>(() =>
    loadConversationsFromStorage()
  );
  // One pending sync per conversation id. A single shared timer would let a
  // switch to another conversation cancel the previous one's pending save.
  const syncTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // Track the active conversation for the refresh merge without re-creating the
  // callback on every switch.
  const currentConversationIdRef = useRef<string | null>(null);
  useEffect(() => {
    currentConversationIdRef.current = currentConversationId;
  }, [currentConversationId]);

  // Merge server history into the local list. The SERVER is authoritative for
  // which conversations exist: one missing from the server was deleted on
  // another device, so drop it — except the chat in progress locally, whose
  // first turn may not have synced yet. A plain union would resurrect deletes.
  const refreshConversations = useCallback(() => {
    fetchAiConversations()
      .then((serverConversations) => {
        const serverList = serverConversations.map(summaryToConversation);
        setConversations((local) => {
          const byId = new Map<string, SavedConversation>();
          for (const c of serverList) byId.set(c.id, c);
          for (const c of local) {
            const server = byId.get(c.id);
            if (server) {
              if (c.updatedAt.getTime() > server.updatedAt.getTime()) byId.set(c.id, c);
            } else if (c.id === currentConversationIdRef.current) {
              byId.set(c.id, c);
            }
          }
          const merged = Array.from(byId.values()).sort(
            (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()
          );
          saveConversationsToStorage(merged);
          return merged;
        });
      })
      .catch(() => {
        // Network unavailable — keep localStorage copy
      });
  }, []);

  // Pull the latest server history on mount.
  useEffect(() => {
    refreshConversations();
  }, [refreshConversations]);

  // Clear any pending sync timers on unmount.
  useEffect(() => {
    const timers = syncTimeoutsRef.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
    };
  }, []);

  // Debounced server sync after messages change — keyed per conversation.
  const syncToServer = useCallback((conversation: SavedConversation) => {
    const timers = syncTimeoutsRef.current;
    const existing = timers.get(conversation.id);
    if (existing) clearTimeout(existing);
    timers.set(
      conversation.id,
      setTimeout(() => {
        timers.delete(conversation.id);
        saveAiConversation(conversationToSummary(conversation)).catch(() => {
          // Fire-and-forget — localStorage is the fallback
        });
      }, 1000)
    );
  }, []);

  // Auto-save current conversation when messages change
  useEffect(() => {
    if (messages.length === 0) return;

    const now = new Date();

    setConversations((prev) => {
      let updated: SavedConversation[];
      let targetConversation: SavedConversation;

      if (currentConversationId) {
        updated = prev.map((c) =>
          c.id === currentConversationId
            ? { ...c, messages, title: generateTitle(messages), updatedAt: now }
            : c
        );
        targetConversation = updated.find((c) => c.id === currentConversationId)!;
      } else {
        const newId = Date.now().toString();
        setCurrentConversationId(newId);
        targetConversation = {
          id: newId,
          title: generateTitle(messages),
          messages,
          createdAt: now,
          updatedAt: now,
          source: 'webapp',
        };
        updated = [targetConversation, ...prev];
      }

      saveConversationsToStorage(updated);
      if (targetConversation) syncToServer(targetConversation);
      return updated;
    });
  }, [messages, currentConversationId, syncToServer]);

  const addMessage = useCallback((message: ChatMessage) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  const updateMessage = useCallback((id: string, updates: Partial<ChatMessage>) => {
    setMessages((prev) =>
      prev.map((msg) => (msg.id === id ? { ...msg, ...updates } : msg))
    );
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setCurrentConversationId(null);
  }, []);

  const loadConversation = useCallback((id: string) => {
    const conversation = conversations.find((c) => c.id === id);
    if (conversation) {
      setMessages(conversation.messages);
      setCurrentConversationId(id);
    }
  }, [conversations]);

  const deleteConversation = useCallback((id: string) => {
    // Cancel any pending sync so a debounced PUT can't resurrect it after delete.
    const pending = syncTimeoutsRef.current.get(id);
    if (pending) {
      clearTimeout(pending);
      syncTimeoutsRef.current.delete(id);
    }
    setConversations((prev) => {
      const updated = prev.filter((c) => c.id !== id);
      saveConversationsToStorage(updated);
      return updated;
    });
    deleteAiConversation(id).catch(() => {});
    if (currentConversationId === id) {
      setMessages([]);
      setCurrentConversationId(null);
    }
  }, [currentConversationId]);

  const startNewConversation = useCallback(() => {
    setMessages([]);
    setCurrentConversationId(null);
  }, []);

  return (
    <ChatContext.Provider
      value={{
        messages,
        currentConversationId,
        addMessage,
        updateMessage,
        clearMessages,
        conversations,
        loadConversation,
        deleteConversation,
        startNewConversation,
        refreshConversations,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}
